"use client";

import { useState, useEffect } from "react";
import { getRouteApi } from "@tanstack/react-router";
import { OrganisationsTable } from "@/components/organisations-table";
import { useOrganisations } from "@/hooks/use-organisations";

// Bound to the /organisations/ route via getRouteApi (rather than importing the
// Route directly) to avoid a circular import between the route file and this page.
const routeApi = getRouteApi("/organisations/");

export default function OrganisationsPage() {
  const { page = 1, pageSize = 10, search = "" } = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  // Page index used by the table is 0-based; the URL/API are 1-based.
  const pageIndex = page - 1;

  // Local mirror of the committed (URL) search so the input stays responsive
  // while we debounce writes to the URL.
  const [searchQuery, setSearchQuery] = useState(search);

  // Keep the input in sync when the URL search changes externally
  // (e.g. navigating back to the list).
  useEffect(() => {
    setSearchQuery(search);
  }, [search]);

  // Debounce the search input, then commit it to the URL and reset to page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== search) {
        navigate({
          search: (prev) => ({ ...prev, search: searchQuery, page: 1 }),
          replace: true,
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, search, navigate]);

  // Fetch organisations with pagination
  const { data, isLoading, error } = useOrganisations({
    pageNumber: page, // API uses 1-based page numbers
    pageSize: pageSize,
    search: search || undefined,
  });

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        {/* Page Header */}
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Organisations</h1>
          <p className="text-muted-foreground">
            Manage and view all your organisations
          </p>
        </div>

        {/* Organisations Table */}
        <OrganisationsTable
          data={data?.data || []}
          isLoading={isLoading}
          error={error ? String(error) : null}
          totalCount={data?.count || 0}
          pageIndex={pageIndex}
          pageSize={pageSize}
          searchQuery={searchQuery}
          onPageChange={(nextPageIndex) =>
            navigate({
              search: (prev) => ({ ...prev, page: nextPageIndex + 1 }),
            })
          }
          onPageSizeChange={(nextPageSize) =>
            navigate({
              search: (prev) => ({ ...prev, pageSize: nextPageSize, page: 1 }),
            })
          }
          onSearchChange={setSearchQuery}
        />
      </div>
    </div>
  );
}
